import React, { useState, useEffect, useLayoutEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../style/DishPage.css";
import dishesData from '../assets/dishes2.json';

const DishCard = ({ title, review, restaurant, overallRating, image, id }) => {
  const fallbackImage = "https://dummyimage.com/300x200/e0e0e0/757575&text=No+Review+Image";
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const imagePrefix = isLocalhost ? "" : "https://webdev.cse.buffalo.edu/hci/teams/indecisive";
  return (
    <Link to={id ? `/reviewPost/${id}` : '#'} style={{ textDecoration: "none", color: "inherit" }} className="dish-card-link">
      <div className="dish-card">
        {image && <img className="dish-card-img" alt={title || 'Review Image'} src={image ? `${imagePrefix}${image}` : fallbackImage} />}
        <div className="dish-card-info">
          {title && <h3>{title}</h3>}
          {!title && <h3>Review</h3>}
          {overallRating && (
            <p className="dish-card-rating">
              <strong>Rating:</strong> {overallRating}/5
            </p>
          )}
          {review && <p className="dish-card-review">{review}</p>}
          {restaurant && (
            <p className="dish-card-restaurant">
              <strong>By:</strong> {restaurant}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
};

// Helper function to return a CSS class for colored tags
const getTagClassName = (tag) => {
  if (typeof tag !== 'string') return '';
  const lowerTag = tag.toLowerCase().trim();
  
  // Spice Level
  if (lowerTag === 'hot') return 'tag-hot';
  if (lowerTag === 'medium') return 'tag-medium';
  if (lowerTag === 'mild') return 'tag-mild';
  if (lowerTag === 'no spice') return 'tag-no-spice';
  
  // Dietary (Vegetarian/Vegan)
  if (lowerTag === 'vegetarian' || lowerTag === 'vegan') return 'tag-veggie';
  
  // Dietary Restrictions / Allergens (-Free)
  if (lowerTag === 'gluten free') return 'tag-gluten-free';
  if (lowerTag === 'soy free') return 'tag-soy-free';
  if (lowerTag === 'dairy free') return 'tag-dairy-free';
  if (lowerTag === 'nut free') return 'tag-nut-free';
  
  // Contains Specific Allergen
  if (lowerTag === 'contains seafood') return 'tag-contains-seafood';
  
  // Cuisine Types
  if (lowerTag === 'mexican') return 'tag-mexican';
  if (lowerTag === 'chinese') return 'tag-chinese';
  if (lowerTag === 'japanese') return 'tag-japanese';
  if (lowerTag === 'korean') return 'tag-korean';
  if (lowerTag === 'indian') return 'tag-indian';
  if (lowerTag === 'american') return 'tag-american';
  if (lowerTag === 'italian') return 'tag-italian';
  
  return '';
};

const DishPage = () => {
  const navigate = useNavigate();
  const { dishId: dishIdParam } = useParams();
  const dishId = parseInt(dishIdParam, 10);
  const dish = !isNaN(dishId) ? dishesData.find(d => d.id === dishId) : undefined;

  // Local state
  const [isFavorite, setIsFavorite] = useState(false);
  const [userAttributes, setUserAttributes] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiReviews, setApiReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const blockedContent = JSON.parse(sessionStorage.getItem("blockedPosts"));
  const [blockingList, setBlockingList] = useState([]);
  const [fetched, setFetched] = useState(false);


  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const imagePrefix = isLocalhost ? "" : "https://webdev.cse.buffalo.edu/hci/teams/indecisive";

  // Extract dish properties (ensure tags defaults to an array)
  const { title, restaurantName, image, description, reviews = [], tags = [] } = dish || {};

  // Helper: Check if user is logged in
  const checkIsLoggedIn = () => {
    return !!sessionStorage.getItem("token") && !!sessionStorage.getItem("user");
  };
  const loggedIn = checkIsLoggedIn();
  const userToken = sessionStorage.getItem("token");
  const userId = sessionStorage.getItem("user");

  // Fetch reviews for this dish from the API
  useEffect(() => {
    const fetchReviews = async () => {
      if (!dishId) return;
      
      setIsLoading(true);
      try {
        // Construct the API URL - get posts where attributes.dishID matches our dishId
        const url = `${process.env.REACT_APP_API_PATH}/posts?parentID=&limit=100`;
        
        // Prepare headers based on login status
        const headers = {
          "Content-Type": "application/json",
        };
        
        if (userToken) {
          headers["Authorization"] = `Bearer ${userToken}`;
        }

        const response = await fetch(url, { method: "GET", headers });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch reviews: ${response.status}`);
        }
        
        const result = await response.json();
        
        // Filter posts to only include reviews for this dish
        if (result && result[0] && Array.isArray(result[0])) {
          // Enhanced filtering to check both dishID and restaurantName
          const dishReviews = result[0].filter(post => 
            post.attributes && 
            post.attributes.dishID && 
            parseInt(post.attributes.dishID, 10) === dishId &&
            (!post.attributes.restaurantName || 
             post.attributes.restaurantName === restaurantName)
          );
          
          // console.log("THIS IS THE BLOCKING LIST");
          // console.log(blockingList);
          // Map the API response to the format expected by the DishCard component
          const formattedReviews = dishReviews.filter((rev) => !blockingList.includes(rev.id)).map(post => ({
            id: post.id,
            title: post.attributes.title || 'Review',
            review: post.content || '',
            restaurant: post.attributes.restaurant || 'A user',
            overallRating: post.attributes.overallRating,
            image: post.attributes.image
          }));
          
          setApiReviews(formattedReviews);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, [dishId, userToken]);

  // Combine static and API reviews, compute average rating
  useEffect(() => {
    if (!dish) return;
    const staticReviews = dish.reviews || [];
    const combinedReviews = [...staticReviews, ...apiReviews];
    setAllReviews(combinedReviews);

    if (combinedReviews.length > 0) {
      const totalRating = combinedReviews.reduce((sum, review) => {
        const rating = parseFloat(review.overallRating) || 5;
        return sum + rating;
      }, 0);
      const avg = totalRating / combinedReviews.length;
      setAverageRating(Math.round(avg * 10) / 10);
      setTotalReviews(combinedReviews.length);
    } else {
      setAverageRating(0);
      setTotalReviews(0);
    }
  }, [dish, apiReviews]);

  // Fetch user data to determine favorites if logged in
  useEffect(() => {
    if (!loggedIn || !dish) return;

    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_PATH}/users/${userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const result = await response.json();
        if (result && result.attributes) {
          setUserAttributes(result.attributes);
          const favorites = result.attributes.favorites || [];
          const isDishFavorited = favorites.some(fav => fav.id === dishId);
          setIsFavorite(isDishFavorited);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [loggedIn, userId, userToken, dishId, dish]);

  const fetchBlockedUsers = async () => {
      try {
        // Fetch user details
        const response = await fetch(
          `${process.env.REACT_APP_API_PATH}/users/${userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              // Authorization: `Bearer ${userToken}`,
            },
          }
        ); 
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Failed to fetch user data. Status:", response.status, "Response:", errorData);
          throw new Error(errorData.message || `Failed to fetch user data (${response.status})`);
        }
  
        const result = await response.json();
        // console.log("RESULTS!");
        // console.log(result);
        sessionStorage.setItem("accountJSON",JSON.stringify(result));
        const newList = [];
        for (let entry in result.attributes.blockedPosts){
          blockingList.push(parseInt(entry));
        }
        // console.log("This is the new List!")
        // console.log(blockingList);
        setFetched(true);
      }
      catch (err) {
        return;
      }
    }

    useLayoutEffect(() => {
      fetchBlockedUsers();
    })

  // If dish is not found, render a not-found message
  if (!dish && !isLoading) {
    return (
      <div className="dish-page-container">
        <div className="no-content-message" style={{ paddingTop: '5rem' }}>
          <h2>Oops! Dish Not Found</h2>
          <p>We couldn't find a dish with ID: {dishIdParam || 'invalid'}.</p>
          <Link to="/">Go back to homepage</Link>
        </div>
      </div>
    );
  }

  // Event handler: Navigate to Add Review page (only if logged in)
  const handleNewPost = () => {
    if (loggedIn) {
      navigate(`/dish/${dishIdParam}/new-post`, {
        state: {
          dishId: dishId,
          dishTitle: title,
          restaurantName: restaurantName
        }
      });
    }
  };

  // Event handler: Toggle favorite status (only if logged in)
  const handleAddToFavorites = async () => {
    if (!loggedIn) return;
    
    setIsLoading(true);
    try {
      const dishToSave = {
        id: dishId,
        title: title,
        image: image,
        restaurant: restaurantName,
        description: description
      };

      let updatedFavorites = [...(userAttributes.favorites || [])];
      if (isFavorite) {
        updatedFavorites = updatedFavorites.filter(fav => fav.id !== dishId);
      } else {
        updatedFavorites.push(dishToSave);
      }
      
      const updatedAttributes = { ...userAttributes, favorites: updatedFavorites };
      
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/users/${userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({ attributes: updatedAttributes }),
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to update favorites");
      }
      
      setUserAttributes(updatedAttributes);
      setIsFavorite(!isFavorite);
      // console.log(`Toggled favorite for dish ID ${dishId}:`, !isFavorite);
    } catch (error) {
      console.error("Error updating favorites:", error);
     
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dish-page-container">
      <div className="dish-info-section">
        <div className="dish-page-header">
          <div className="dish-title-group">
            <h1 className="dish-page-title">{title || "Dish Title"}</h1>
            {restaurantName && (
              <p className="dish-page-restaurant">{restaurantName}</p>
            )}
          </div>
          <div className="header-buttons">
            {loggedIn && (
              <button
                className={`favorite-button ${isFavorite ? 'is-favorite' : ''}`}
                onClick={handleAddToFavorites}
                aria-pressed={isFavorite}
                disabled={isLoading}
              >
                <span className="heart-icon" aria-hidden="true">
                  {isFavorite ? '❤️' : '♡'}
                </span>
                {isLoading ? 'Loading...' : (isFavorite ? 'Favorited' : 'Favorite')}
              </button>
            )}
            {loggedIn && (
              <button className="dish-new-post-button" onClick={handleNewPost}>
                Add Review
              </button>
            )}
          </div>
        </div>

        <div className="dish-main-content">
          <div className="dish-main-image-container">
            <img
              className="dish-main-image"
              src={image ? `${imagePrefix}${image}` : 'https://dummyimage.com/700x438/e0e0e0/aaaaaa&text=No+Main+Image'}
              alt={title || "Dish Image"}
            />
          </div>

          <div className="dish-details-container">
            {description && (
              <div className="dish-description-container">
                <p className="dish-description">{description}</p>
              </div>
            )}

            {Array.isArray(tags) && tags.length > 0 && (
              <div className="dish-tags-container">
                {tags.map((tag, index) => {
                  const specificTagClass = getTagClassName(tag);
                  const finalClassName = `dish-tag ${specificTagClass}`.trim();
                  const key = typeof tag === 'string' && tag ? tag : `tag-${index}`;
                  return (
                    <span key={key} className={finalClassName}>
                      {tag}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <section className="dish-reviews-section">
        <h2 className="dish-reviews-heading">User Reviews & Posts</h2>
        {isLoading ? (
          <div className="loading-message">
            <p>Loading reviews...</p>
          </div>
        ) : (!Array.isArray(allReviews) || allReviews.length === 0) ? (
          <div className="no-content-message">
            <h3>{loggedIn ? "Be the first to add a review!" : "Log in to add the first review!"}</h3>
            {!loggedIn && <p>Reviews from others will appear here.</p>}
          </div>
        ) : (
          <div className="reviews-grid">
            {allReviews.map((rev) => {
              // console.log("MAP");
              // console.log(rev);
              const {
                id: reviewId,
                title: reviewTitle = 'Review',
                review: reviewText = '',
                restaurant: reviewRestaurant = 'A user',
                overallRating,
                image: reviewImage,
              } = rev || {};
              const key = reviewId != null ? reviewId : `review-${Math.random()}`;
              return (
                <DishCard
                  key={key}
                  title={reviewTitle}
                  review={reviewText}
                  restaurant={reviewRestaurant}
                  overallRating={overallRating}
                  image={reviewImage ? `${imagePrefix}${reviewImage}` : undefined}
                  id={reviewId}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default DishPage;